import {
  ContentCategory,
  ContentCompetency,
  ContentCompetencyProficiency,
} from '@backrs_ui/common-ui';

import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/system';

import { ProsperContent } from '../molecules/ProsperContent';
import { ProsperHeader } from '../molecules/ProsperHeader';

type ProsperPageProps = Readonly<{
  contentCategories: ContentCategory[];
  contentCompetencies: ContentCompetency[];
  competencyProficiencies: ContentCompetencyProficiency[];
}>;

export const ProsperPage = ({
  contentCategories,
  contentCompetencies,
  competencyProficiencies,
}: ProsperPageProps) => {
  // first we create a map of content competency id to proficiency
  const proficiencyMap = Object.fromEntries(
    competencyProficiencies.map(
      ({ content_competency_id, proficiency }: ContentCompetencyProficiency) => [
        content_competency_id,
        proficiency,
      ],
    ),
  ) as Record<number, number>;

  // now we can count the number of uncompleted, level one, and level two competency proficiencies
  const { uncompletedCount, levelOneCount, levelTwoCount } = contentCompetencies.reduce(
    (acc, competency) => {
      const proficiency = proficiencyMap[competency.id];

      switch (proficiency) {
        case 1:
          return {
            ...acc,
            levelOneCount: acc.levelOneCount + 1,
          };
        case 2:
          return {
            ...acc,
            levelTwoCount: acc.levelTwoCount + 1,
          };
        default:
          return {
            ...acc,
            uncompletedCount: acc.uncompletedCount + 1,
          };
      }
    },
    { uncompletedCount: 0, levelOneCount: 0, levelTwoCount: 0 },
  );

  // finally, we append the proficiency to each competency so that it's easier for the dashboard content to render
  const contentCompetenciesWithProficiency = contentCompetencies.map((competency) => ({
    ...competency,
    proficiency: proficiencyMap[competency.id] || 0,
  }));

  return (
    <Box sx={parentContainerStyle}>
      <ProsperHeader
        uncompletedCount={uncompletedCount}
        levelOneCount={levelOneCount}
        levelTwoCount={levelTwoCount}
      />
      <ProsperContent
        contentCategories={contentCategories}
        contentCompetenciesWithProficiencies={contentCompetenciesWithProficiency}
      />
    </Box>
  );
};

const parentContainerStyle: SxProps<Theme> = {
  height: '100%',
  width: '100%',
  overflowY: 'scroll',
  padding: 2,
  maxWidth: '1512px',
  margin: '0 auto',
  alignSelf: 'center',
};
